import { useEffect, useState } from 'react'
import { Coins, TrendingUp, Clock, Lock } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead, Button, Modal, Field, Rows, Empty, Badge, NumberInput, Tabs } from '../components/UI'
import { CoinIcon } from '../components/Market'
import { useAuth, toast } from '../store'
import { get, post } from '../lib/api'
import { clsx, fa, num, asArray} from '../lib/format'
import { useSeo } from '../lib/seo'

export default function Staking() {
  useSeo({
    title: 'استیکینگ ارز دیجیتال با سود روزشمار',
    description: 'دارایی دیجیتال خود را در طرح‌های استیکینگ ارزینو سپرده کنید و سود دوره‌ای دریافت کنید.',
    keywords: 'استیکینگ، سود ارز دیجیتال، سرمایه گذاری رمزارز',
    canonical: '/staking',
  })
  const { user } = useAuth()
  const [plans, setPlans] = useState<any[] | null>(null)
  const [mine, setMine] = useState<any[] | null>(null)
  const [tab, setTab] = useState('plans')
  const [selected, setSelected] = useState<any>(null)
  const [amount, setAmount] = useState('')
  const [loading, setLoading] = useState(false)

  const load = () => {
    get('/staking/plans').then((r: any) => setPlans(asArray(r))).catch(() => setPlans([]))
    if (user) get('/staking/mine').then((r: any) => setMine(asArray(r))).catch(() => setMine([]))
  }
  useEffect(load, [user])

  const stake = async () => {
    setLoading(true)
    try {
      await post('/staking/stake', { plan_id: selected.id, amount: Number(amount) })
      toast.ok('سرمایه‌گذاری با موفقیت انجام شد')
      setSelected(null); setAmount(''); load()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  const unstake = async (id: number) => {
    try { await post(`/staking/unstake/${id}`); toast.ok('اصل سرمایه به کیف پول شما بازگشت'); load() }
    catch (e: any) { toast.err(e.message) }
  }

  return (
    <Shell>
      <SectionHead title="استیکینگ ارزینو" subtitle="دارایی خود را قفل کنید و سود روزانه دریافت کنید" />

      <Card className="relative mb-6 overflow-hidden !p-8">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-up/20 blur-[90px]" />
        <div className="relative grid items-center gap-6 md:grid-cols-[1.4fr_1fr]">
          <div>
            <span className="chip mb-4 border-up/30 bg-up/10 text-up"><Coins className="h-3.5 w-3.5" /> درآمد غیرفعال</span>
            <h2 className="mb-3 text-2xl font-extrabold text-white sm:text-3xl">تا {fa('۳۲')}٪ سود سالانه</h2>
            <p className="max-w-lg text-sm leading-7 text-slate-400">
              سود به‌صورت لحظه‌ای محاسبه و در پایان دوره همراه با اصل سرمایه به کیف پول شما واریز می‌شود.
              امکان برداشت زودهنگام اصل سرمایه در هر زمان وجود دارد.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[['سود روزانه', 'محاسبه لحظه‌ای'], ['بدون کارمزد', 'ورود و خروج'], ['امنیت کامل', 'کیف پول سرد']].map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-white/[.07] bg-white/[.02] p-3">
                <div className="mb-1 text-xs font-bold text-white">{t}</div>
                <div className="text-[10px] leading-5 text-slate-500">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Tabs value={tab} onChange={setTab} className="mb-5"
        items={[{ key: 'plans', label: 'طرح‌های فعال' }, { key: 'mine', label: 'سرمایه‌گذاری‌های من' }]} />

      {tab === 'plans' ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {plans === null ? <div className="sm:col-span-2 lg:col-span-3"><Rows n={3} h="h-40" /></div>
            : plans.map((p) => (
              <Card key={p.id} hover className="relative overflow-hidden">
                <div className="pointer-events-none absolute -left-10 -top-10 h-28 w-28 rounded-full opacity-30 blur-2xl"
                  style={{ background: p.color }} />
                <div className="relative">
                  <div className="mb-4 flex items-center gap-3">
                    <CoinIcon symbol={p.currency} color={p.color} />
                    <div>
                      <div className="font-bold text-white">{p.faName}</div>
                      <div className="num text-[11px] text-slate-500" dir="ltr">{p.currency}</div>
                    </div>
                    <Badge kind="up">
                      <span className="num">{num(p.apr, 1)}٪</span> سالانه
                    </Badge>
                  </div>

                  <div className="mb-4 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-2.5">
                      <div className="mb-1 flex items-center gap-1 text-[10px] text-slate-500"><Clock className="h-3 w-3" /> مدت</div>
                      <div className="num text-slate-200">{fa(p.days)} روز</div>
                    </div>
                    <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-2.5">
                      <div className="mb-1 flex items-center gap-1 text-[10px] text-slate-500"><Lock className="h-3 w-3" /> حداقل</div>
                      <div className="num text-slate-200">{num(p.minAmount, 4)}</div>
                    </div>
                  </div>

                  <div className="mb-1.5 flex justify-between text-[11px] text-slate-500">
                    <span>ظرفیت تکمیل‌شده</span><span className="num">{num(p.fillPercent, 1)}٪</span>
                  </div>
                  <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-white/[.06]">
                    <div className="h-full rounded-full bg-gradient-to-l from-brand-600 to-up" style={{ width: `${p.fillPercent}%` }} />
                  </div>

                  <Button variant="up" className="w-full" onClick={() => (user ? setSelected(p) : toast.err('ابتدا وارد حساب کاربری شوید'))}>
                    <TrendingUp className="h-4 w-4" /> سرمایه‌گذاری
                  </Button>
                </div>
              </Card>
            ))}
        </div>
      ) : (
        <Card className="!p-0">
          <div className="p-2">
            {!user ? <Empty text="برای مشاهده، وارد حساب کاربری شوید" />
              : mine === null ? <div className="p-3"><Rows n={3} /></div>
              : mine.length === 0 ? <Empty text="هنوز سرمایه‌گذاری فعالی ندارید" icon={<Coins className="h-6 w-6" />} />
              : mine.map((s) => (
                <div key={s.id} className="border-b border-white/[.05] p-4 last:border-0">
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <CoinIcon symbol={s.currency} size={32} />
                    <div>
                      <div className="num text-sm font-semibold text-white">{num(s.amount, 6)} {s.currency}</div>
                      <div className="num text-[11px] text-slate-500">{num(s.apr, 1)}٪ سالانه — {fa(s.days)} روز</div>
                    </div>
                    <Badge kind={s.status === 'active' ? 'up' : 'muted'}>
                      {s.status === 'active' ? 'فعال' : s.status === 'finished' ? 'پایان‌یافته' : 'لغو شده'}
                    </Badge>
                    <div className="mr-auto text-left">
                      <div className="text-[11px] text-slate-500">سود دریافتی</div>
                      <div className="num text-sm font-semibold text-up">{num(s.rewardPaid, 8)}</div>
                    </div>
                    {s.status === 'active' && (
                      <Button variant="ghost" className="!py-2 text-xs" onClick={() => unstake(s.id)}>برداشت زودهنگام</Button>
                    )}
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/[.06]">
                    <div className="h-full rounded-full bg-gradient-to-l from-brand-600 to-up transition-all" style={{ width: `${s.progress}%` }} />
                  </div>
                  <div className="mt-1.5 flex justify-between text-[10px] text-slate-500">
                    <span className="num">پیشرفت {num(s.progress, 1)}٪</span>
                    <span className="num">سود تخمینی کل: {num(s.estimatedTotal, 8)} {s.currency}</span>
                  </div>
                </div>
              ))}
          </div>
        </Card>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title={`سرمایه‌گذاری ${selected?.faName ?? ''}`}>
        {selected && (
          <>
            <div className="mb-4 grid grid-cols-3 gap-2 text-center text-xs">
              {[['سود سالانه', `${num(selected.apr, 1)}٪`], ['مدت قرارداد', `${fa(selected.days)} روز`], ['حداقل مبلغ', num(selected.minAmount, 4)]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                  <div className="mb-1 text-[10px] text-slate-500">{k}</div>
                  <div className="num font-semibold text-white">{v}</div>
                </div>
              ))}
            </div>
            <Field label={`مبلغ سرمایه‌گذاری (${selected.currency})`}>
              <NumberInput value={amount} onChange={setAmount} suffix={selected.currency} />
            </Field>
            {Number(amount) > 0 && (
              <div className="mb-4 rounded-xl border border-up/20 bg-up/[.06] p-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-400">سود تخمینی در پایان دوره</span>
                  <span className="num font-bold text-up">
                    {num((Number(amount) * (selected.apr / 100) * selected.days) / 365, 8)} {selected.currency}
                  </span>
                </div>
              </div>
            )}
            <Button variant="up" loading={loading} onClick={stake} className="w-full !py-3" disabled={!Number(amount)}>
              تأیید و قفل دارایی
            </Button>
          </>
        )}
      </Modal>
    </Shell>
  )
}
